import { NgClass } from "@angular/common";
import {
  Component,
  DestroyRef,
  effect,
  inject,
  Inject,
  OnInit,
  Optional,
  signal,
} from "@angular/core";
import { HotToastRef } from "@ngxpert/hot-toast";

export type CustomToastType = "success" | "error" | "info" | "warning";

export type CustomToastData = {
  title: string;
  message?: string;
  duration: number;
  type: CustomToastType;
  isExpandable: boolean;
  showConfirmation?: boolean;
};

@Component({
  standalone: true,
  imports: [NgClass],
  selector: "app-custom-toast-v1",
  templateUrl: "./custom-toast-v1.html",
  styleUrls: ["./custom-toast-v1.css"],
})
export class CustomToastV1 implements OnInit {
  private readonly destroyRef = inject(DestroyRef);

  readonly isTimerStopped = signal(false);
  readonly remainingTime = signal(0);
  readonly pauseTimer = signal(false);

  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private dismissTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    @Optional() @Inject(HotToastRef) public toastRef: HotToastRef<CustomToastData>,
  ) {
    effect(() => {
      if (this.pauseTimer() || this.isTimerStopped()) {
        this.clearTimer();
        this.clearDismissTimeout();
        return;
      }
      this.startCountdown();
      this.scheduleAutoDismiss();
    });

    effect(() => {
      if (
        this.isTimerStopped() ||
        this.pauseTimer() ||
        this.remainingTime() > 0
      ) {
        return;
      }
      this.closeToast();
    });

    this.destroyRef.onDestroy(() => {
      this.clearTimer();
      this.clearDismissTimeout();
    });
  }

  ngOnInit(): void {
    const durationMs = this.toastRef?.data?.duration ?? 3000;
    this.remainingTime.set(Math.max(1, Math.ceil(durationMs / 1000)));
    this.isTimerStopped.set(false);
  }

  stopTimer(): void {
    this.toastRef?.updateToast({ duration: Infinity });
    this.isTimerStopped.set(true);
  }

  private startCountdown(): void {
    this.clearTimer();
    this.timerInterval = setInterval(() => {
      if (this.isTimerStopped() || this.pauseTimer()) {
        return;
      }
      const next = this.remainingTime() - 1;
      this.remainingTime.set(Math.max(0, next));
    }, 1000);
  }

  private scheduleAutoDismiss(): void {
    this.clearDismissTimeout();
    const ms = this.remainingTime() * 1000;
    if (ms <= 0) {
      this.closeToast();
      return;
    }
    this.dismissTimeout = setTimeout(() => this.closeToast(), ms);
  }

  private closeToast(): void {
    if (this.isTimerStopped()) {
      return;
    }
    this.clearTimer();
    this.clearDismissTimeout();
    this.toastRef?.close({ dismissedByAction: false });
  }

  private clearTimer(): void {
    if (this.timerInterval !== null) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  }

  private clearDismissTimeout(): void {
    if (this.dismissTimeout !== null) {
      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = null;
    }
  }
}
